import React, { useState } from 'react';
import { Facebook, Instagram, Mail, Phone, MapPin, Globe } from 'lucide-react';
import { BsPinterest } from 'react-icons/bs';
import { goHome, goToAbout, goToContact, navigate } from '../utils/navigation';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement subscription functionality
    console.log('Subscribe with email:', email);
    setEmail('');
    alert('Thank you for subscribing!');
  };
  const socialLinks = [
    { icon: <Facebook className="h-5 w-5" />, href: 'https://www.facebook.com/profile.php?id=61578083672940', label: 'Facebook' },
    { icon: <Instagram className="h-5 w-5" />, href: 'https://www.instagram.com/durgapujanetwork/', label: 'Instagram' },
    { icon: <BsPinterest className="h-5 w-5" />, href: 'https://in.pinterest.com/durgapujanetwork/', label: 'Pinterest' }
  ];

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Celebrations', href: '#search' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Vote', href: '#vote' },
    { name: 'Shop', href: '#shop' },
    { name: 'Contact', href: '/contact' }
  ];

  const handleLinkClick = (href: string) => {
    if (href.startsWith('#')) {
      // Navigate to home first, then scroll to section
      const sectionId = href.replace('#', '');
      
      // Check if we're already on home page
      if (window.location.pathname === '/') {
        // Already on home, just scroll to section
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home first, then scroll to section
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      // Navigate to different page using updated navigation utility
      navigate(href);
    }
  };

  return (
    <footer id="contact" className="bg-[#121927] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex justify-center mb-4">
              <div className="bg-white rounded-full shadow-lg ring-1 ring-dpn-pink/20 cursor-pointer hover:shadow-xl transition-all duration-200 p-3" onClick={goHome}>
                <img 
                  src="/logo.png" 
                  alt="Durga Puja Network" 
                  className="h-16 w-16 object-contain"
                  onError={(e) => {
                    // Fallback to Globe icon if image fails to load
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <Globe className="h-14 w-14 text-dpn-pink hidden" />
              </div>
            </div>
            
            {/* Cultural Archive Description */}
            <div className="mb-6 text-center md:text-left">
              <p className="font-lucy-rose text-gray-300 leading-relaxed text-sm">
                Durga Puja Network, a global Durga Puja archive, to help you connect, share and celebrate world's most vibrant cultural festival through art, community and heritage. We celebrate the vibrant community spirit that makes this Durga Puja an UNESCO-recognized Global Cultural Heritage. Dugga Dugga !
              </p>
            </div>
            <div className="flex justify-center md:justify-start space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-[#1a2235] hover:bg-dpn-pink rounded-full transition-colors duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="pt-20 m-5 mt-0">
            <h3 className="font-runtime text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleLinkClick(link.href)}
                    className="font-lucy-rose text-gray-300 hover:text-dpn-pink transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="pt-20 m-5 mt-0">
            <h3 className="font-runtime text-lg font-bold mb-4">Resources</h3>
            <ul className="space-y-2 font-lucy-rose text-gray-300">
              <li>
                <a href="#" className="hover:text-dpn-pink transition-colors duration-200">
                  Cultural Heritage Guide
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-dpn-pink transition-colors duration-200">
                  Organizer Toolkit
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-dpn-pink transition-colors duration-200">
                  Photography Guidelines
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-dpn-pink transition-colors duration-200">
                  Committee Support
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-dpn-pink transition-colors duration-200">
                  Media Kit
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info & Subscribe */}
          <div className="pt-20 m-5 mt-0">
            <h3 className="font-runtime text-lg font-bold mb-4">Get in Touch</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-dpn-light-green" />
                <a
                  href="tel:+919810693616"
                  className="font-lucy-rose text-gray-300 hover:text-dpn-pink transition-colors duration-200"
                >
                  9810693616
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-dpn-light-green" />
                <a
                  href="mailto:hello@DurgaPuja.Network"
                  className="font-lucy-rose text-gray-300 hover:text-dpn-pink transition-colors duration-200"
                >
                  hello@DurgaPuja.Network
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <address className="font-lucy-rose text-gray-300 not-italic">
                  Ground Floor, Ghosh Villa, 8/K/1 Baishnabghata Bye Lane P. O. Naktala, P.S. Netaji Nagar, Kolkata Pin Code- 700047
                </address>
              </div>
            </div>
            
            {/* Subscribe Section */}
            <div>
              <h4 className="font-lucy-rose font-medium text-white mb-2">Subscribe to Updates</h4>
              <form onSubmit={handleSubscribe} className="flex">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="px-4 py-2 bg-[#1a2235] text-white rounded-l-md w-full focus:outline-none focus:ring-1 focus:ring-dpn-pink text-sm"
                />
                <button
                  type="submit"
                  className="bg-dpn-pink hover:bg-dpn-raspberry text-white px-4 py-2 rounded-r-md transition-colors duration-200"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col space-y-2">
              <p className="font-lucy-rose text-sm text-gray-400 text-center md:text-left">
                Durga Puja Network © 2025–35, Mind's Workstation India Pvt. Ltd.  All rights reserved.
              </p>
              <p className="font-lucy-rose text-sm text-gray-400 text-center md:text-left">
                CIN: U74999WB2022PTC250672 | GST: 19AAPCM3337G1Z6 | Start-up India Reg No: DIPP115871
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <button
                onClick={() => handleLinkClick('/terms')}
                className="font-lucy-rose text-sm text-gray-400 hover:text-dpn-pink transition-colors duration-200"
              >
                Terms & Conditions
              </button>
              <button
                onClick={() => handleLinkClick('/disclaimer')}
                className="font-lucy-rose text-sm text-gray-400 hover:text-dpn-pink transition-colors duration-200"
              >
                Disclaimer
              </button>
              <button
                onClick={() => handleLinkClick('/privacy')}
                className="font-lucy-rose text-sm text-gray-400 hover:text-dpn-pink transition-colors duration-200"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;